import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { isDue } from '../components/PublishStatus';
import { useProducts } from '../product/ProductContext';
import { storage } from '../product/productStorage';
import {
    formatWhen, needsPolling, normalizeJob, normalizeJobs, platformIcon, platformLabel, shortReason,
} from '../utils/publishing';
import '../components/publish.css';

// The publishing queue: what's scheduled, what went out and what failed, with cancel and retry.
// Posts are published from the Content plan; this screen keeps an eye on them.

const POLL_MS = 20 * 1000;

const TABS = [
    { id: 'upcoming', label: 'Upcoming', statuses: ['queued', 'running', 'processing'] },
    { id: 'published', label: 'Published', statuses: ['published'] },
    { id: 'failed', label: 'Failed', statuses: ['failed'] },
];

const STATUS_CHIPS = {
    queued: { label: 'Scheduled', icon: 'schedule', tone: 'is-neutral' },
    running: { label: 'Publishing', icon: '', tone: 'is-accent' },
    processing: { label: 'Processing', icon: '', tone: 'is-accent' },
    published: { label: 'Published', icon: 'check_circle', tone: 'is-success' },
    failed: { label: 'Failed', icon: 'error', tone: 'is-danger' },
    cancelled: { label: 'Cancelled', icon: 'block', tone: 'is-muted' },
};

const time = (value) => {
    const ms = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(ms) ? 0 : ms;
};

// Upcoming: soonest first. Published and failed: newest first.
const SORTS = {
    upcoming: (a, b) => (time(a.scheduledAt) || time(a.createdAt)) - (time(b.scheduledAt) || time(b.createdAt)),
    published: (a, b) => (time(b.publishedAt) || time(b.updatedAt)) - (time(a.publishedAt) || time(a.updatedAt)),
    failed: (a, b) => (time(b.updatedAt) || time(b.createdAt)) - (time(a.updatedAt) || time(a.createdAt)),
};

const errorText = (error, fallback) => {
    const message = error?.response?.data?.error;
    return typeof message === 'string' && message.trim() && error.response.status < 500 ? message : fallback;
};

// The first image, or a glyph for video, text only, or an image that won't load
const Thumb = ({ media }) => {
    const [failed, setFailed] = useState('');
    const showImage = media?.kind === 'image' && media.url && media.url !== failed;
    return (
        <span className="ls-publish-thumb" aria-hidden="true">
            {showImage
                ? <img src={media.url} alt="" loading="lazy" onError={() => setFailed(media.url)} />
                : <Icon name={media ? (media.kind === 'video' ? 'movie' : 'image') : 'notes'} size={20} />}
        </span>
    );
};

const PublishingScreen = () => {
    const navigate = useNavigate();
    const { currentProduct } = useProducts();
    const [tab, setTab] = useState(() => (TABS.some(item => item.id === storage.get('publishingTab')) ? storage.get('publishingTab') : 'upcoming'));
    const [jobs, setJobs] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [accounts, setAccounts] = useState(null); // OAuth accounts that can post; null until loaded
    const [busy, setBusy] = useState({}); // { jobId: 'cancel' | 'retry' }
    const [notice, setNotice] = useState(null); // { severity, message }
    const [refreshing, setRefreshing] = useState(false);
    const requestId = useRef(0);

    const load = useCallback(async ({ quiet = false } = {}) => {
        const id = ++requestId.current;
        if (!quiet) setStatus(previous => (previous === 'ready' ? 'ready' : 'loading'));
        try {
            const list = normalizeJobs(await api.listPublishJobs({ limit: 100 }));
            if (id !== requestId.current) return;
            setJobs(list);
            setStatus('ready');
        } catch (e) {
            if (id !== requestId.current) return;
            if (!quiet) setStatus('error');
        }
    }, []);

    useEffect(() => {
        setStatus('loading');
        load();
        let cancelled = false;
        api.getSocialAccounts()
            .then(data => !cancelled && setAccounts((Array.isArray(data?.accounts) ? data.accounts : [])
                .filter(account => account?.kind === 'oauth' && account.status === 'connected')))
            .catch(() => !cancelled && setAccounts(null));
        return () => {
            cancelled = true;
        };
    }, [load, currentProduct?.id]);

    const shouldPoll = needsPolling(jobs);
    useEffect(() => {
        if (!shouldPoll) return undefined;
        const timer = setInterval(() => {
            if (document.visibilityState !== 'hidden') load({ quiet: true });
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [shouldPoll, load]);

    const groups = useMemo(() => TABS.reduce((acc, item) => ({
        ...acc,
        [item.id]: jobs.filter(job => item.statuses.includes(job.status)).sort(SORTS[item.id]),
    }), {}), [jobs]);

    const changeTab = (id) => {
        setTab(id);
        storage.set('publishingTab', id);
    };

    const refresh = async () => {
        setRefreshing(true);
        await load({ quiet: status === 'ready' });
        setRefreshing(false);
    };

    const act = async (job, action) => {
        setBusy(previous => ({ ...previous, [job.id]: action }));
        setNotice(null);
        try {
            const updated = action === 'cancel' ? await api.cancelPublishJob(job.id) : await api.retryPublishJob(job.id);
            if (updated && typeof updated === 'object') {
                const next = normalizeJob(updated);
                setJobs(previous => previous.map(item => (item.id === next.id ? next : item)));
            }
            setNotice({
                severity: 'success',
                message: action === 'cancel'
                    ? `Cancelled the ${platformLabel(job.platform)} post. Nothing will go out.`
                    : `Trying the ${platformLabel(job.platform)} post again. It's back under Upcoming.`,
            });
            load({ quiet: true });
        } catch (e) {
            setNotice({
                severity: 'error',
                message: errorText(e, action === 'cancel' ? "Couldn't cancel that post. Please try again." : "Couldn't retry that post. Please try again in a moment."),
            });
        } finally {
            setBusy(previous => {
                const { [job.id]: removed, ...rest } = previous;
                return rest;
            });
        }
    };

    const renderChip = (job) => {
        const chip = STATUS_CHIPS[job.status] || STATUS_CHIPS.queued;
        return (
            <span className={`ls-publish-chip ${chip.tone}`}>
                {chip.icon ? <Icon name={chip.icon} size={14} /> : <span className="ls-spinner" />}
                {chip.label}
            </span>
        );
    };

    const renderTime = (job) => {
        if (job.status === 'published') return job.publishedAt ? `Published ${formatWhen(job.publishedAt)}` : 'Published';
        if (job.status === 'failed') return `Failed${job.updatedAt ? ` ${formatWhen(job.updatedAt)}` : ''}${job.attempts ? ` · ${job.attempts} ${job.attempts === 1 ? 'try' : 'tries'}` : ''}`;
        if (job.status === 'running' || job.status === 'processing') return 'Publishing now';
        return isDue(job) ? 'Posting in a minute or two' : `Scheduled ${formatWhen(job.scheduledAt)}`;
    };

    const renderJob = (job, index) => {
        const thumb = job.media[0];
        const action = busy[job.id];
        return (
            <li key={job.id} className={`ls-publish-job is-${job.status}`} style={{ '--i': index }}>
                <span className="ls-publish-glyph" aria-hidden="true"><Icon name={platformIcon(job.platform)} size={18} /></span>
                <div className="ls-publish-job-main">
                    <div className="ls-publish-job-head">
                        <span className="ls-publish-handle">{job.accountHandle ? `${['facebook', 'linkedin'].includes(job.platform) ? '' : '@'}${job.accountHandle}` : platformLabel(job.platform)}</span>
                        <span className="ls-publish-job-platform">{platformLabel(job.platform)}</span>
                        {renderChip(job)}
                    </div>
                    <p className="ls-publish-job-text">{job.text.replace(/\s+/g, ' ').trim() || <span className="ls-muted">No text</span>}</p>
                    <div className="ls-publish-job-meta">
                        <span>{renderTime(job)}</span>
                        {job.media.length > 1 && <span>{job.media.length} files</span>}
                    </div>
                    {job.status === 'failed' && (
                        <p className="ls-publish-job-error" title={job.lastError || undefined}>
                            <Icon name="error" size={14} />{shortReason(job.lastError, 200)}
                        </p>
                    )}
                </div>
                <Thumb media={thumb} />
                <div className="ls-publish-job-actions">
                    {job.status === 'queued' && (
                        <button type="button" className="ls-btn ls-btn-secondary" onClick={() => act(job, 'cancel')} disabled={Boolean(action)}>
                            {action === 'cancel' ? <span className="ls-spinner" /> : <Icon name="close" size={16} />}Cancel
                        </button>
                    )}
                    {job.status === 'failed' && (
                        <button type="button" className="ls-btn ls-btn-secondary" onClick={() => act(job, 'retry')} disabled={Boolean(action)}>
                            {action === 'retry' ? <span className="ls-spinner" /> : <Icon name="refresh" size={16} />}Retry
                        </button>
                    )}
                    {job.status === 'published' && job.externalUrl && (
                        <a className="ls-btn ls-btn-secondary" href={job.externalUrl} target="_blank" rel="noopener noreferrer">
                            <Icon name="open_in_new" size={16} />Open
                        </a>
                    )}
                </div>
            </li>
        );
    };

    const renderSkeleton = () => (
        <ul className="ls-publish-jobs" aria-hidden="true">
            {[0, 1, 2].map(index => (
                <li key={index} className="ls-publish-job">
                    <span className="ls-skeleton ls-publish-glyph" />
                    <div className="ls-publish-job-main">
                        <div className="ls-publish-job-head"><span className="ls-skeleton-text" style={{ width: 140 }} /></div>
                        <p className="ls-publish-job-text"><span className="ls-skeleton-text" style={{ width: '90%' }} /></p>
                        <div className="ls-publish-job-meta"><span className="ls-skeleton-text" style={{ width: 120 }} /></div>
                    </div>
                    <span className="ls-skeleton ls-publish-thumb" />
                    <div className="ls-publish-job-actions"><span className="ls-skeleton" style={{ width: 92, height: 34, borderRadius: 20 }} /></div>
                </li>
            ))}
        </ul>
    );

    const noAccounts = Array.isArray(accounts) && accounts.length === 0;

    const renderEmpty = () => {
        const copy = {
            upcoming: { icon: 'schedule_send', title: 'Nothing scheduled', text: 'Open a post in your Content plan and choose Publish. Post it now or schedule it, and it shows up here until it goes out.' },
            published: { icon: 'task_alt', title: 'Nothing published yet', text: 'Posts light speed publishes for you land here, with a link to each one.' },
            failed: { icon: 'verified', title: 'No failed posts', text: 'When a platform turns a post down, it shows up here with the reason and a Retry button.' },
        }[tab];
        return (
            <div className="ls-publish-empty-state">
                <Icon name={copy.icon} size={28} className="ls-publish-empty-icon" />
                <div className="ls-card-title">{copy.title}</div>
                <p className="ls-empty">{copy.text}</p>
                <div className="ls-publish-empty-actions">
                    {noAccounts && (
                        <Link className="ls-btn ls-btn-secondary" to="/dashboard/channels"><Icon name="hub" size={18} />Connect an account</Link>
                    )}
                    {tab === 'upcoming' && (
                        <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate('/dashboard/plan')}>
                            <Icon name="calendar_month" size={18} />Open Content plan
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const list = groups[tab] || [];

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">{currentProduct?.name ? `Plan · ${currentProduct.name}` : 'Plan'}</div>
                    <h1 className="ls-page-title">Publishing</h1>
                </div>
                <div className="ls-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={refresh} disabled={refreshing || status === 'loading'}>
                        {refreshing ? <span className="ls-spinner" /> : <Icon name="refresh" size={18} />}Refresh
                    </button>
                    <button type="button" className="ls-btn ls-btn-primary" onClick={() => navigate('/dashboard/plan')}>
                        <Icon name="calendar_month" size={18} />Content plan
                    </button>
                </div>
            </div>

            {noAccounts && (
                <Alert
                    severity="info"
                    action={<Link className="ls-btn ls-btn-secondary ls-publish-retry" to="/dashboard/channels">Social accounts</Link>}
                >
                    Connect Instagram, Facebook, Threads, LinkedIn or Pinterest to publish from your plan. Accounts linked by handle only can't post.
                </Alert>
            )}

            {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert>}

            <section className="ls-card ls-publish-queue" aria-labelledby="publish-queue-title" aria-busy={status === 'loading'}>
                <div className="ls-card-header ls-publish-queue-head">
                    <h2 className="ls-card-title" id="publish-queue-title">Queue</h2>
                    <div className="ls-segmented ls-publish-tabs" role="group" aria-label="Show">
                        {TABS.map(item => (
                            <button key={item.id} type="button" aria-pressed={tab === item.id} onClick={() => changeTab(item.id)}>
                                {item.label}
                                <span
                                    key={status === 'ready' ? groups[item.id].length : 'loading'}
                                    className={`ls-publish-tab-count ls-count-in${item.id === 'failed' && groups.failed.length ? ' is-danger' : ''}`}
                                >
                                    {status === 'ready' ? groups[item.id].length : '–'}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {status === 'loading' && renderSkeleton()}
                {status === 'error' && (
                    <Alert severity="error" action={<button type="button" className="ls-btn ls-btn-secondary ls-publish-retry" onClick={() => load()}>Try again</button>}>
                        Couldn't load your publishing queue. Check your connection, then try again.
                    </Alert>
                )}
                {status === 'ready' && (list.length ? (
                    <ul className="ls-publish-jobs ls-stagger" key={tab}>{list.map(renderJob)}</ul>
                ) : renderEmpty())}
            </section>
        </>
    );
};

export default PublishingScreen;
